import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircle, Clapperboard } from "lucide-react";
import * as React from "react";
import { acceptTokenResponse } from "@/api/auth";
import { ApiError, errorMessage } from "@/api/client";
import type { InviteInfo } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-[380px]">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold">
          <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-fg">
            <Clapperboard className="size-4" />
          </span>
          Reel Studio
        </div>
        <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <div className="mt-1 text-sm text-muted">{subtitle}</div>}
          <div className="mt-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div role="alert" className="mb-4 flex items-start gap-2 rounded-md bg-danger-subtle px-3 py-2 text-sm text-danger-text">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      {errorMessage(error)}
    </div>
  );
}

function safeNext(next: string | undefined) {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  const { next } = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const login = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new ApiError(res.status, body.code, body.detail, body);
      acceptTokenResponse(body);
    },
    onSuccess: () => navigate({ to: safeNext(next) }),
  });
  return (
    <AuthCard title="Sign in" subtitle="Use the email and password from your invite.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <FormError error={login.error} />
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={login.isPending} className="mt-1 w-full">
          Sign in
        </Button>
        <p className="text-center text-xs text-subtle">No account? Ask an admin for an invite link.</p>
      </form>
    </AuthCard>
  );
}

function useHashToken() {
  return React.useMemo(() => new URLSearchParams(location.hash.slice(1)).get("t") ?? "", []);
}

function useLinkInfo(token: string) {
  return useQuery({
    queryKey: ["invite", token],
    enabled: !!token,
    retry: false,
    queryFn: async (): Promise<InviteInfo> => {
      const res = await fetch("/api/invites/inspect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json();
      if (!res.ok) throw new ApiError(res.status, body.code, body.detail, body);
      return body;
    },
  });
}

function LinkProblem({ error }: { error?: unknown }) {
  return (
    <AuthCard title="This link can't be used" subtitle={error ? errorMessage(error) : "The link is incomplete."}>
      <p className="text-sm text-muted">Ask an admin to make you a new link. Links work once and expire after a few days.</p>
      <Button asChild variant="secondary" className="mt-4 w-full">
        <a href="/login">Go to sign in</a>
      </Button>
    </AuthCard>
  );
}

async function postJson(path: string, body: unknown) {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new ApiError(res.status, data.code, data.detail, data);
  acceptTokenResponse(data);
}

export function InvitePage() {
  const token = useHashToken();
  const info = useLinkInfo(token);
  const navigate = useNavigate();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  React.useEffect(() => {
    if (info.data?.email_hint) setEmail(info.data.email_hint);
  }, [info.data?.email_hint]);
  const accept = useMutation({
    mutationFn: () => postJson("/api/invites/accept", { token, email, password, display_name: name }),
    onSuccess: () => {
      history.replaceState(null, "", "/");
      navigate({ to: "/" });
    },
  });
  if (!token) return <LinkProblem />;
  if (info.isLoading) return <AuthCard title="Checking your invite…">{null}</AuthCard>;
  if (info.error || !info.data) return <LinkProblem error={info.error} />;
  if (info.data.purpose !== "signup") return <LinkProblem error={new Error("This is a password reset link.")} />;
  const i = info.data;
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthCard
      title="Join Reel Studio"
      subtitle={
        <>
          {i.invited_by ? <b className="text-fg">{i.invited_by}</b> : "An admin"} invited you as{" "}
          <b className="text-fg">{i.role === "admin" ? "a super admin" : i.newspaper_role === "admin" ? "an admin" : "a member"}</b>
          {i.role !== "admin" && i.newspapers.length > 0 && (
            <>
              {" "}
              of {i.newspapers.map((n) => n.name).join(", ")}
            </>
          )}
          .
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) accept.mutate();
        }}
      >
        <FormError error={accept.error} />
        <Field label="Your name" htmlFor="name">
          <Input id="name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" required readOnly={!!i.email_hint} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
        </Field>
        <Field label="Password" htmlFor="pw" hint="At least 10 characters. A few unrelated words work well.">
          <Input id="pw" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="Password again" htmlFor="pw2" error={mismatch ? "The two passwords differ." : null}>
          <Input id="pw2" type="password" required value={confirm} aria-invalid={mismatch} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={accept.isPending} className="w-full">
          Create account
        </Button>
      </form>
    </AuthCard>
  );
}

export function ResetPage() {
  const token = useHashToken();
  const info = useLinkInfo(token);
  const navigate = useNavigate();
  const [password, setPassword] = React.useState("");
  const reset = useMutation({
    mutationFn: () => postJson("/api/password-reset/accept", { token, password }),
    onSuccess: () => {
      history.replaceState(null, "", "/");
      navigate({ to: "/" });
    },
  });
  if (!token) return <LinkProblem />;
  if (info.isLoading) return <AuthCard title="Checking your link…">{null}</AuthCard>;
  if (info.error || !info.data) return <LinkProblem error={info.error} />;
  return (
    <AuthCard title="Choose a new password" subtitle={info.data.email_hint}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
      >
        <FormError error={reset.error} />
        <Field label="New password" htmlFor="pw" hint="At least 10 characters. You'll be signed out everywhere else.">
          <Input id="pw" type="password" required minLength={10} autoFocus value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={reset.isPending} className="w-full">
          Set password and sign in
        </Button>
      </form>
    </AuthCard>
  );
}
